import { api } from './api.js';
import { actions, appStore } from '../store/appStore.js';
import { DataAdapter } from '../services/dataAdapter.js';
import { Delivery, CreatePackLineRequest } from '../types/index.js';

interface DataServiceInitOptions {
  force?: boolean;
}

class DataService {
  private currentCycleId: number | null = null;
  private facilityId: number | null = null;
  private isInitialized = false;

  // force=true is used when transitioning from unauthenticated to authenticated,
  // to make sure that if if the initial requests to load data failed because of
  // an auth failure, we will automatically retry once auth is successful.
  async initialize(options: DataServiceInitOptions = {}) {
    if (this.isInitialized && !options.force) return;

    try {
      // Load initial data
      await this.loadFacilities();
      await this.loadCycles();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize data service:', error);
    }
  }

  async loadFacilities() {
    try {
      actions.setLoading(true);
      const facilities = await api.getFacilities();
      actions.setFacilities(facilities);

      if (facilities.length > 0) {
        const state = appStore.getState();
        let selectedFacility = state.selectedFacility;
        // Validate persisted selectedFacility exists in current facilities, fallback to first if invalid/null
        if (!selectedFacility || !facilities.find(f => f.id === selectedFacility!.id)) {
          selectedFacility = facilities[0];
        }
        // Always update store with validated selection
        actions.setSelectedFacility(selectedFacility);
        this.facilityId = selectedFacility.id;
      }
    } catch (error) {
      console.error('Failed to load facilities:', error);
      actions.addAlert('Failed to load facilities', 'error');
    } finally {
      actions.setLoading(false);
    }
  }

  async loadCycles() {
    try {
      actions.setLoading(true);

      // Get the current cycle based on PT timezone rules
      const currentCycle = await api.getCurrentCycle();

      // Store the current cycle
      actions.setCurrentCycle(currentCycle);
      this.currentCycleId = currentCycle.id;

      if (this.facilityId) {
        await this.loadRoutePrefixData(currentCycle.id, this.facilityId);
        await this.loadPackLines(currentCycle.id, this.facilityId);
      }
    } catch (error) {
      console.error('dataService.loadCycles error:', error);
      actions.addAlert('Failed to load current cycle', 'error');
    } finally {
      actions.setLoading(false);
    }
  }

  async loadRoutePrefixData(cycleId: number, facilityId?: number, showLoading: boolean = true) {
    try {
      if (showLoading) {
        actions.setLoading(true);
      }
      this.currentCycleId = cycleId;
      const useFacilityId = facilityId ?? this.facilityId;
      if (!useFacilityId) {
        console.warn('No facility ID available for loading route prefix data');
        return;
      }
      const routePrefixes = await api.getRoutePrefixesByCycleId(cycleId, useFacilityId);
      actions.setRoutePrefixes(routePrefixes);
    } catch (error) {
      console.error('Failed to load route prefix data:', error);
      actions.addAlert('Failed to load route prefixes', 'error');
    } finally {
      if (showLoading) {
        actions.setLoading(false);
      }
    }
  }

  async loadPackLines(cycleId?: number, facilityId?: number, showLoading: boolean = true) {
    try {
      if (showLoading) {
        actions.setLoading(true);
      }
      const currentCycleId = cycleId || this.currentCycleId;
      if (!currentCycleId) {
        console.warn('No cycle ID available for loading pack lines');
        return;
      }
      const useFacilityId = facilityId ?? this.facilityId;
      if (!useFacilityId) {
        console.warn('No facility ID available for loading pack lines');
        return;
      }
      const packLines = await api.getPackLinesByCycleId(currentCycleId, useFacilityId);
      const adaptedPackLines = packLines.map(pl => DataAdapter.adaptPackLineForComponents(pl));
      actions.setPackLines(adaptedPackLines);
      // Update linked pack line. It would be nice if it were a reference to an object
      // in the pack lines collection, but it's a copy, so we have to keep it up to date
      // manually.
      const state = appStore.getState();
      if (state.linkedPackLine) {
        const updatedPackLine = adaptedPackLines.find(pl => pl.id === state.linkedPackLine?.id);
        if (updatedPackLine) {
          actions.setLinkedPackLine(updatedPackLine);
        }
      }
    } catch (error) {
      console.error('Failed to load pack lines:', error);
      actions.addAlert('Failed to load pack lines', 'error');
    } finally {
      if (showLoading) {
        actions.setLoading(false);
      }
    }
  }

  async loadRoutesByRoutePrefix(cycleId: number, routePrefixId: number, facilityId: number, showLoading: boolean = true) {
    try {
      if (showLoading) {
        actions.setLoading(true);
      }
      const routes = await api.getRoutesByRoutePrefix(cycleId, routePrefixId, facilityId);
      actions.setExpandedRoutesData(routes);
      return routes;
    } catch (error) {
      console.error('Failed to load routes:', error);
      actions.addAlert('Failed to load routes', 'error');
      return [];
    } finally {
      if (showLoading) {
        actions.setLoading(false);
      }
    }
  }

  async loadDeliveriesByRouteId(routeId: number, showLoading: boolean = true) {
    try {
      if (showLoading) {
        actions.setLoading(true);
      }
      const deliveries = await api.getDeliveriesByRouteId(routeId);
      actions.setExpandedDeliveriesData(deliveries);
      return deliveries;
    } catch (error) {
      console.error('Failed to load deliveries:', error);
      actions.addAlert('Failed to load deliveries', 'error');
      return [];
    } finally {
      if (showLoading) {
        actions.setLoading(false);
      }
    }
  }

  // API methods for modifying data
  async createPackLine(packLineData: CreatePackLineRequest) {
    try {
      actions.setLoading(true);
      const result = await api.createPackLine(packLineData);
      // Reload pack lines after successful creation
      await this.loadPackLines();
      return result;
    } catch (error) {
      console.error('Failed to create pack line:', error);
      actions.addAlert('Failed to create pack line', 'error');
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }

  async managePackSession(packLineId: number, action: 'start' | 'break' | 'end', employeeTempCount?: number, employeeExemptCount?: number) {
    try {
      actions.setLoading(true);
      const result = await api.managePackSession(packLineId, action, employeeTempCount, employeeExemptCount);
      // Reload pack lines after session change
      await this.loadPackLines();
      // Also reload route prefix data to update the Route Prefixes table
      if (this.currentCycleId && this.facilityId) {
        await this.loadRoutePrefixData(this.currentCycleId, this.facilityId);
      }

      // Refresh RoutesList expanded data if a route or route prefix is expanded
      const state = appStore.getState();
      if (state.expandedRoute) {
        await this.loadDeliveriesByRouteId(state.expandedRoute.routeId, false);
      }
      if (state.expandedRoutePrefix && state.currentCycle && state.selectedFacility) {
        await this.loadRoutesByRoutePrefix(
          state.expandedRoutePrefix.cycleId,
          state.expandedRoutePrefix.routePrefixId,
          state.selectedFacility.id,
          false
        );
      }

      return result;
    } catch (error) {
      console.error('Failed to manage pack session:', error);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }

  async linkDeviceToPackline(packLineId: number) {
    try {
      actions.setLoading(true);
      const result = await api.linkDeviceToPackline(packLineId);

      // First, set a temporary linked pack line so the adapter knows which one is linked
      const currentState = appStore.getState();
      const packLine = currentState.packLines.find(pl => pl.id === packLineId);
      if (packLine) {
        actions.setLinkedPackLine(packLine);
      }

      // Then reload pack lines after linking (this will now correctly set isLinked=true)
      await this.loadPackLines();

      // Also reload route prefix data to update the Route Prefixes table
      if (this.currentCycleId && this.facilityId) {
        await this.loadRoutePrefixData(this.currentCycleId, this.facilityId);
      }

      return result;
    } catch (error) {
      console.error('Failed to link device to pack line:', error);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }

  async scanDeliveryQR(packLineId: number, qrCode: string) {
    try {
      const result = await api.scanDeliveryQR(packLineId, qrCode);

      // Reload pack lines to update data in PackLinesSnapshot (without showing loading overlay)
      await this.loadPackLines(undefined, undefined, false);

      // Reload pack line details to get updated delivery data (without showing loading overlay)
      const state = appStore.getState();

      // Also reload route prefix data to update the Route Prefixes table (without showing loading overlay)
      if (this.currentCycleId && this.facilityId) {
        await this.loadRoutePrefixData(this.currentCycleId, this.facilityId, false);
      }

      // Refresh RoutesList expanded data if a route or route prefix is expanded
      if (state.expandedRoute) {
        await this.loadDeliveriesByRouteId(state.expandedRoute.routeId, false);
      }
      if (state.expandedRoutePrefix && state.currentCycle && state.selectedFacility) {
        await this.loadRoutesByRoutePrefix(
          state.expandedRoutePrefix.cycleId,
          state.expandedRoutePrefix.routePrefixId,
          state.selectedFacility.id,
          false
        );
      }

      return result;
    } catch (error) {
      console.error('dataService.scanDeliveryQR error:', error);
      throw error;
    }
  }

  async updateDeliveryStatus(deliveryId: number, status: 'pending' | 'omitted' | 'manual_entry', submittedBy?: string, packLineId?: number) {
    try {
      const result = await api.updateDeliveryStatus(deliveryId, status, submittedBy, packLineId);

      // Reload pack lines to update data in PackLinesSnapshot (without showing loading overlay)
      await this.loadPackLines(undefined, undefined, false);

      // Reload pack line details to get updated delivery data (without showing loading overlay)
      const state = appStore.getState();

      // Also reload route prefix data to update the Route Prefixes table  (without showing loading overlay)
      if (this.currentCycleId && this.facilityId) {
        await this.loadRoutePrefixData(this.currentCycleId, this.facilityId, false);
      }

      // Refresh RoutesList expanded data if a route or route prefix is expanded
      if (state.expandedRoute) {
        await this.loadDeliveriesByRouteId(state.expandedRoute.routeId, false);
      }
      if (state.expandedRoutePrefix && state.currentCycle && state.selectedFacility) {
        await this.loadRoutesByRoutePrefix(
          state.expandedRoutePrefix.cycleId,
          state.expandedRoutePrefix.routePrefixId,
          state.selectedFacility.id,
          false
        );
      }

      return result;
    } catch (error) {
      console.error('dataService.updateDeliveryStatus error:', error);
      throw error;
    }
  }

  // Handle facility selection changes
  async onFacilityChange(facilityId: number) {
    if (facilityId !== this.facilityId) {
      this.facilityId = facilityId;
      if (this.currentCycleId) {
        try {
          actions.setLoading(true);
          await this.loadRoutePrefixData(this.currentCycleId, facilityId);
          await this.loadPackLines(this.currentCycleId, facilityId);
        } catch (error) {
          console.error('Failed to change facility:', error);
          actions.addAlert('Failed to load data for selected facility', 'error');
        } finally {
          actions.setLoading(false);
        }
      }
    }
  }

  // Set facility ID (should be called during app initialization)
  setFacilityId(facilityId: number) {
    this.facilityId = facilityId;
  }
}

export const dataService = new DataService();
